import React, {
  RefObject,
  useEffect,
} from 'react';
import clsx from 'clsx';
import type {
  UserMessageDTO,
} from 'dtos';
import Table from 'core/components/VirtualTable';
import {
  VirtualTableInstance,
} from 'core/components/VirtualTable/types';
import {
  useMixpanelTrack,
  useSidePanel,
  useTableReset,
} from 'core/hooks';
import useSignalREffect from 'views/EMR/hooks/signalR';
import columns from 'views/EMR/Messages/columns';
import getInboxMessagesData from 'views/EMR/Messages/transport';

function getOuterRowClass({ original }: { original: UserMessageDTO }) {
  const isSkeleton = Object.keys(original).length === 0;

  return clsx(
    '!w-[calc(100%-8px)] left-1 mb-2 overflow-hidden last:mb-0',
    {
      '!rounded-md !border !border-gray-100 bg-white shadow-sm': !isSkeleton,
      '!border-transparent !bg-transparent !shadow-none hover:!border-transparent': isSkeleton,
    },
  );
}

export default function Inbox() {
  const mixpanelTrack = useMixpanelTrack();
  const { openPanel } = useSidePanel();
  const {
    tableInstanceRef,
  } = useTableReset();

  useEffect(() => {
    mixpanelTrack('navigatedToMessages');
  }, [mixpanelTrack]);

  useSignalREffect('OnUserMessageStateUpdated', () => {
    tableInstanceRef.current?.refetch?.();
  });

  const handleRowClick = async ({ toDoId }: UserMessageDTO) => {
    if (toDoId) {
      mixpanelTrack('openedToDoFromMessages', { source: 'messages' });
      openPanel('todo', { todoId: toDoId });
    }
  };

  return (
    <>
      <div className="px-4 pb-4 pt-5">
        <h1 className="text-xl font-semibold leading-none text-gray-900">Notifications</h1>
        <p className="mt-3 text-sm text-gray-500">
          Recent to-do assignments, mentions, and updates.
        </p>
      </div>
      <div className="relative flex flex-col flex-1 overflow-y-hidden px-4 pb-4">
        <div className="h-full w-full ">
          <Table<UserMessageDTO>
            instanceRef={tableInstanceRef as RefObject<VirtualTableInstance>}
            getData={getInboxMessagesData}
            getRowId={(row) => row.userMessageId
              ?? `${row.createdOn ?? ''}-${row.activityType ?? ''}-${row.actorUserId ?? ''}`}
            dataKey="notifications"
            columns={columns}
            emptySlot="No messages found"
            defaultPagination={{
              pageSize: 25,
              pageIndex: 0,
            }}
            classes={{
              header: '!hidden',
              scrollContainer: 'justify-center items-center',
              outerRow: getOuterRowClass,
              row: 'before:!hidden overflow-hidden bg-white transition-colors hover:bg-slate-50',
              cell: '!border-b-0 !px-0 !py-0',
            }}
            onRowClick={(table, row) => { handleRowClick(row.original); }}
            loadingSlot="Loading notifications..."
          />
        </div>
      </div>
    </>
  );
}
